import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { ScrollArea } from '../ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Pill, Loader2, Info, Search, Plus, XCircle, Thermometer, Shield } from 'lucide-react';

// Alternative Names Tag Input Component
const AlternativeNamesInput = ({ value = [], onChange, t }) => {
  const [inputValue, setInputValue] = useState('');

  const handleAddName = () => {
    const trimmed = inputValue.trim();
    if (trimmed && !value.includes(trimmed)) {
      onChange([...value, trimmed]);
      setInputValue('');
    }
  };

  const handleRemoveName = (nameToRemove) => {
    onChange(value.filter(n => n !== nameToRemove));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddName();
    }
  };

  return (
    <div className="border rounded-lg p-3 bg-blue-50/50 dark:bg-blue-950/20">
      <Label className="flex items-center gap-2 mb-2 text-sm">
        <Search className="h-4 w-4 text-blue-600" />
        {t('catalog.form.alternativeNames')}
      </Label>
      <p className="text-xs text-muted-foreground mb-2">
        {t('catalog.form.alternativeNamesHint')}
      </p>
      
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {value.map((name, idx) => (
            <Badge 
              key={idx} 
              variant="secondary" 
              className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 hover:bg-blue-200 pr-1 text-xs"
            >
              {name}
              <button
                type="button"
                onClick={() => handleRemoveName(name)}
                className="ml-1 hover:text-red-600 p-0.5"
              >
                <XCircle className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
      
      <div className="flex gap-2">
        <Input
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={t('catalog.form.alternativeNamesPlaceholder')}
          className="flex-1 h-8 text-sm"
        />
        <Button 
          type="button" 
          variant="outline" 
          size="sm"
          onClick={handleAddName}
          disabled={!inputValue.trim()}
          className="h-8"
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export const MedicationFormPanel = ({ 
  medication, 
  isOpen, 
  onClose, 
  onSubmit 
}) => {
  const { t } = useTranslation('medications');
  const [activeTab, setActiveTab] = useState('basic');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    generic_name: '',
    commercial_name: '',
    active_ingredient: '',
    concentration: '',
    form: '',
    manufacturer: '',
    is_controlled: false,
    requires_refrigeration: false,
    alternative_names: []
  });

  const formOptions = [
    { value: 'tablet', label: t('catalog.forms.tablet') },
    { value: 'capsule', label: t('catalog.forms.capsule') },
    { value: 'syrup', label: t('catalog.forms.syrup') },
    { value: 'injection', label: t('catalog.forms.injection') },
    { value: 'cream', label: t('catalog.forms.cream') },
    { value: 'ointment', label: t('catalog.forms.ointment') },
    { value: 'drops', label: t('catalog.forms.drops') },
    { value: 'inhaler', label: t('catalog.forms.inhaler') },
    { value: 'patch', label: t('catalog.forms.patch') },
    { value: 'suppository', label: t('catalog.forms.suppository') },
    { value: 'solution', label: t('catalog.forms.solution') },
    { value: 'powder', label: t('catalog.forms.powder') },
    { value: 'other', label: t('catalog.forms.other') }
  ];

  useEffect(() => {
    if (isOpen) {
      if (medication) {
        setFormData({
          generic_name: medication.generic_name || '',
          commercial_name: medication.commercial_name || '',
          active_ingredient: medication.active_ingredient || '',
          concentration: medication.concentration || '',
          form: medication.form || '',
          manufacturer: medication.manufacturer || '',
          is_controlled: medication.is_controlled || false,
          requires_refrigeration: medication.requires_refrigeration || false,
          alternative_names: medication.alternative_names || []
        });
      } else {
        setFormData({
          generic_name: '',
          commercial_name: '',
          active_ingredient: '',
          concentration: '',
          form: '',
          manufacturer: '',
          is_controlled: false,
          requires_refrigeration: false,
          alternative_names: []
        });
      }
      setActiveTab('basic');
    }
  }, [medication, isOpen]);

  const handleSubmit = async () => {
    if (!formData.generic_name || !formData.active_ingredient || !formData.concentration || !formData.form) {
      return;
    }
    
    setLoading(true);
    try {
      await onSubmit(formData);
      onClose();
    } catch (error) {
      console.error('Error saving medication:', error);
    }
    setLoading(false);
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-xl overflow-hidden flex flex-col p-0">
        <SheetHeader className="px-6 py-4 border-b bg-gradient-to-r from-[#E63946]/5 to-transparent">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-[#E63946]/10 flex items-center justify-center">
              <Pill className="h-6 w-6 text-[#E63946]" />
            </div>
            <div>
              <SheetTitle className="text-lg font-semibold">
                {medication?.id ? t('catalog.form.editTitle') : t('catalog.form.title')}
              </SheetTitle>
              <SheetDescription>
                {medication?.id ? t('catalog.form.editDescription') : t('catalog.form.description')}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="px-6 py-2 border-b justify-start rounded-none bg-transparent">
            <TabsTrigger value="basic" className="data-[state=active]:bg-[#E63946]/10 data-[state=active]:text-[#E63946]">
              <Info className="h-4 w-4 mr-1" />
              {t('catalog.form.tabBasic')}
            </TabsTrigger>
            <TabsTrigger value="conditions" className="data-[state=active]:bg-[#E63946]/10 data-[state=active]:text-[#E63946]">
              <Shield className="h-4 w-4 mr-1" />
              {t('catalog.form.tabConditions')}
            </TabsTrigger>
            <TabsTrigger value="ocr" className="data-[state=active]:bg-[#E63946]/10 data-[state=active]:text-[#E63946]">
              <Search className="h-4 w-4 mr-1" />
              {t('catalog.form.tabOCR')}
            </TabsTrigger>
          </TabsList>

          <ScrollArea className="flex-1">
            {/* Basic Info Tab */}
            <TabsContent value="basic" className="p-6 space-y-4 mt-0">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('catalog.form.genericName')} *</Label>
                  <Input
                    value={formData.generic_name}
                    onChange={(e) => setFormData({...formData, generic_name: e.target.value})}
                    placeholder={t('catalog.form.genericNamePlaceholder')}
                    data-testid="medication-generic-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('catalog.form.commercialName')}</Label>
                  <Input
                    value={formData.commercial_name}
                    onChange={(e) => setFormData({...formData, commercial_name: e.target.value})}
                    placeholder={t('catalog.form.commercialNamePlaceholder')}
                    data-testid="medication-commercial-name"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t('catalog.form.activeIngredient')} *</Label>
                <Input
                  value={formData.active_ingredient}
                  onChange={(e) => setFormData({...formData, active_ingredient: e.target.value})}
                  placeholder={t('catalog.form.activeIngredientPlaceholder')}
                  data-testid="medication-active-ingredient"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('catalog.form.concentration')} *</Label>
                  <Input
                    value={formData.concentration}
                    onChange={(e) => setFormData({...formData, concentration: e.target.value})}
                    placeholder={t('catalog.form.concentrationPlaceholder')}
                    data-testid="medication-concentration"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('catalog.form.form')} *</Label>
                  <Select
                    value={formData.form}
                    onValueChange={(value) => setFormData({...formData, form: value})}
                  >
                    <SelectTrigger data-testid="medication-form">
                      <SelectValue placeholder={t('catalog.form.selectForm')} />
                    </SelectTrigger>
                    <SelectContent>
                      {formOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t('catalog.form.manufacturer')}</Label>
                <Input
                  value={formData.manufacturer}
                  onChange={(e) => setFormData({...formData, manufacturer: e.target.value})}
                  placeholder={t('catalog.form.manufacturerPlaceholder')}
                  data-testid="medication-manufacturer"
                />
              </div>
            </TabsContent>

            {/* Conditions Tab */}
            <TabsContent value="conditions" className="p-6 space-y-4 mt-0">
              <div className="flex items-center justify-between p-4 bg-orange-50/50 dark:bg-orange-950/20 rounded-lg border border-orange-200 dark:border-orange-800">
                <div className="flex items-center gap-3">
                  <Shield className="h-5 w-5 text-orange-600" />
                  <div>
                    <Label>{t('catalog.form.isControlled')}</Label>
                    <p className="text-sm text-muted-foreground">{t('catalog.form.isControlledHint')}</p>
                  </div>
                </div>
                <Switch
                  checked={formData.is_controlled}
                  onCheckedChange={(checked) => setFormData({...formData, is_controlled: checked})}
                  data-testid="medication-controlled-switch"
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-blue-50/50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <div className="flex items-center gap-3">
                  <Thermometer className="h-5 w-5 text-blue-600" />
                  <div>
                    <Label>{t('catalog.form.requiresRefrigeration')}</Label>
                    <p className="text-sm text-muted-foreground">{t('catalog.form.requiresRefrigerationHint')}</p>
                  </div>
                </div>
                <Switch
                  checked={formData.requires_refrigeration}
                  onCheckedChange={(checked) => setFormData({...formData, requires_refrigeration: checked})}
                  data-testid="medication-refrigeration-switch"
                />
              </div>
            </TabsContent>

            {/* OCR/AI Tab */}
            <TabsContent value="ocr" className="p-6 space-y-4 mt-0">
              <AlternativeNamesInput
                value={formData.alternative_names}
                onChange={(names) => setFormData({...formData, alternative_names: names})}
                t={t}
              />
              
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  <strong>{t('catalog.form.ocrHintTitle')}:</strong> {t('catalog.form.ocrHintDescription')}
                </p>
              </div>
            </TabsContent>
          </ScrollArea>
        </Tabs>

        <div className="px-6 py-4 border-t bg-muted/30 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            {t('common:cancel')}
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={loading || !formData.generic_name || !formData.active_ingredient || !formData.concentration || !formData.form} 
            className="bg-[#E63946] hover:bg-[#d32836]"
            data-testid="save-medication-btn"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {medication?.id ? t('catalog.form.update') : t('catalog.form.save')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default MedicationFormPanel;
